import { TextField, Typography, Slider, Box, Button } from "@mui/material";
import { useTranslation } from "react-i18next";

import { S } from "./green-load-calculator.styles";
import { LoanInvestmentSettings } from "../types";

type Props = {
    loanInvestmentSettings: LoanInvestmentSettings;
    setLoanInvestmentSettings: (values: LoanInvestmentSettings) => void;
};

export function GreenLoanCalculator(props: Props) {
    const { t } = useTranslation();

    const { loanInvestmentSettings, setLoanInvestmentSettings } = props;

    const handleChange = (fieldName: string, value: string | number) => {
        const newValue = Number(value);

        const newValues = { ...loanInvestmentSettings, [fieldName]: newValue };

        if (fieldName === "propertyPrice") {
            newValues.equity = Math.round(newValue * 0.6);
        }

        setLoanInvestmentSettings(newValues);
    };

    return (
        <S.Calculator>
            <S.Title variant="h5">
                {t(
                    "legacyNext.calculateLoanForGreenInvestment",
                    "calculate loan for green investment"
                )}
            </S.Title>

            <S.Inner>
                <S.Row>
                    <Box>
                        <TextField
                            fullWidth
                            type="number"
                            label={t(
                                "legacyNext.propertyPrice",
                                "property price"
                            )}
                            value={loanInvestmentSettings.propertyPrice}
                            onChange={(e) =>
                                handleChange("propertyPrice", e.target.value)
                            }
                            InputProps={{
                                endAdornment: <span>kr.</span>,
                            }}
                        />
                    </Box>

                    <Box>
                        <TextField
                            fullWidth
                            type="number"
                            label={t("legacyNext.valueImpact", "value impact")}
                            onChange={(e) =>
                                handleChange("valueImpact", e.target.value)
                            }
                            value={loanInvestmentSettings.valueImpact}
                            InputProps={{ endAdornment: <span>%</span> }}
                        />
                    </Box>

                    <Box>
                        <TextField
                            fullWidth
                            type="number"
                            label={t(
                                "legacyNext.improvementLoan",
                                "improvement loan"
                            )}
                            onChange={(e) =>
                                handleChange("improvementLoan", e.target.value)
                            }
                            value={loanInvestmentSettings.improvementLoan}
                            InputProps={{ endAdornment: <span>kr.</span> }}
                        />
                    </Box>

                    <Box>
                        <TextField
                            fullWidth
                            type="number"
                            label={t(
                                "legacyNext.establishmentCosts",
                                "establishment costs"
                            )}
                            onChange={(e) =>
                                handleChange(
                                    "establishmentCosts",
                                    e.target.value
                                )
                            }
                            value={loanInvestmentSettings.establishmentCosts}
                            InputProps={{ endAdornment: <span>kr.</span> }}
                        />
                    </Box>
                </S.Row>
                <S.Row>
                    <Box>
                        <TextField
                            fullWidth
                            type="number"
                            label={t("legacyNext.equity", "equity")}
                            value={loanInvestmentSettings.equity}
                            onChange={(e) =>
                                handleChange("equity", e.target.value)
                            }
                            disabled
                            InputProps={{ endAdornment: <span>kr.</span> }}
                        />
                    </Box>

                    <Box>
                        <TextField
                            fullWidth
                            type="number"
                            label={t(
                                "legacyNext.annualInterestRate",
                                "annual interest rate"
                            )}
                            onChange={(e) =>
                                handleChange(
                                    "annualInterestRate",
                                    e.target.value
                                )
                            }
                            value={loanInvestmentSettings.annualInterestRate}
                            InputProps={{ endAdornment: <span>%</span> }}
                        />
                    </Box>

                    <Box>
                        <Typography gutterBottom>
                            {t(
                                "legacyNext.interestOnlyPeriod",
                                "interest only period"
                            )}
                        </Typography>
                        <S.SliderField>
                            <Slider
                                min={1}
                                max={30}
                                onChange={(_e, newValue) =>
                                    handleChange(
                                        "interestOnlyPeriod",
                                        newValue as number
                                    )
                                }
                            />
                            <TextField
                                size="small"
                                value={
                                    loanInvestmentSettings.interestOnlyPeriod
                                }
                                disabled
                            />
                        </S.SliderField>
                    </Box>

                    <Box>
                        <Typography gutterBottom>
                            {t("legacyNext.loanTerm", "loan term")}
                        </Typography>
                        <S.SliderField>
                            <Slider
                                min={1}
                                max={30}
                                value={loanInvestmentSettings.loanTerm}
                                onChange={(_e, newValue) =>
                                    handleChange("loanTerm", newValue as number)
                                }
                            />
                            <TextField
                                size="small"
                                value={loanInvestmentSettings.loanTerm}
                                disabled
                            />
                        </S.SliderField>
                    </Box>
                </S.Row>
                <S.Row>
                    <Button variant="contained" color="primary">
                        {t("legacyNext.obtainLoan", "obtain loan")}
                    </Button>
                </S.Row>
            </S.Inner>
        </S.Calculator>
    );
}
